
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';
import { FeatureSequenceShowcase } from '@/components/subscription/FeatureSequenceShowcase';

interface FeaturePreviewRouterProps {
  currentPath: string;
}

export function FeaturePreviewRouter({ currentPath }: FeaturePreviewRouterProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const featurePath = currentPath.split('/')[1];
  
  // Map paths to feature names for better user experience
  const getFeatureName = (path: string): string => {
    switch(path) {
      case 'quiz': return 'Adaptive Quiz Assessment';
      case 'analytics': return 'Advanced Analytics';
      case 'flashcards': return 'Adaptive Flash Cards';
      case 'study-notes': return 'Adaptive Study Notes';
      case 'current-affairs': return 'Adaptive Current Affairs';
      case 'learning-path': return 'Adaptive Learning Path';
      case 'video-learning': return 'Adaptive Video Learning';
      case 'mock-test-generator': return 'Adaptive Mock Tests';
      case 'pyq-multiplier': return 'PYQ Multiplier';
      case 'ai-tutoring': return 'AI Learning Studio';
      case 'community': return 'Collaborative Learning';
      case 'playground': return 'Code Playground';
      default: return 'Premium Feature';
    }
  };
  
  // Get subscription tier required for this path
  const getRequiredSubscription = (path: string): 'Basic' | 'Pro' => {
    const proRoutes = ['learning-path', 'video-learning', 'mock-test-generator', 'pyq-multiplier', 'ai-tutoring', 'community', 'playground'];
    return proRoutes.includes(path) ? 'Pro' : 'Basic';
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="w-screen h-screen overflow-hidden bg-white">
      <motion.div 
        className="absolute top-0 left-0 z-50 m-4"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          {/* Completely transparent div with no background */}
          <div 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-6 h-6 relative">
              <img 
                src="/lovable-uploads/79d8bcfe-3886-4213-8a67-03bfd18571be.png" 
                alt="Edaptica Logo" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error("Logo image failed to load");
                }}
              />
            </div>
            <span className="text-xs font-medium bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">Edaptica</span>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Add feature name and subscription requirement */}
      <motion.div 
        className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm text-white text-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <span className="font-medium">{getFeatureName(featurePath)}</span>
        <span className="mx-2">•</span>
        <span>Requires {getRequiredSubscription(featurePath)} Subscription</span>
      </motion.div>
      
      {/* The FeatureSequenceShowcase will render as full screen */}
      <FeatureSequenceShowcase />
    </div>
  );
}
